import React from 'react';
import ErrorMessage from './ErrorMessage';
import ErrorLoggingService from '../service/ErrorLoggingService';

/**
 * Error Boundary Component (SRS-WEB-101)
 * Implements fail-safe mechanisms that default to safe states in case of system failures
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      safeState: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error: error,
      safeState: {
        // Default to safe state - clear sensitive data
        patientData: [],
        userData: null,
        error: null
      }
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    ErrorLoggingService.error(
      'React Error Boundary caught an error',
      error,
      {
        component: 'ErrorBoundary',
        componentStack: errorInfo.componentStack,
        errorBoundary: true
      }
    );

    this.setState({
      errorInfo: errorInfo
    });

    // Attempt to restore safe state
    this.restoreSafeState();
  }

  restoreSafeState = () => {
    try {
      // Clear any potentially corrupted state
      if (this.props.onSafeState) {
        this.props.onSafeState(this.state.safeState);
      }
    } catch (err) {
      ErrorLoggingService.error(
        'Failed to restore safe state',
        err,
        { component: 'ErrorBoundary' }
      );
    }
  };

  handleReset = () => {
    // Reset error boundary state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    // Attempt to reload the application
    if (this.props.onReset) {
      this.props.onReset();
    } else {
      // Default: reload the page
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      // Render fallback UI with safe state
      return (
        <div style={{
          padding: '20px',
          maxWidth: '800px',
          margin: '50px auto',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <ErrorMessage
            error={this.state.error}
            onDismiss={() => this.handleReset()}
            onRetry={() => this.handleReset()}
            showDetails={false}
            autoHide={false}
          />
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fff', borderRadius: '4px' }}>
            <h3 style={{ marginTop: 0 }}>Safe State Restored</h3>
            <p>
              The application has been reset to a safe state. Your data has been preserved where possible.
              Please refresh the page or try again.
            </p>
            <button
              onClick={this.handleReset}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

